import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Hook to check for new unverified members (last 2 days)
 * Only runs for admin users to avoid overloading
 */
export const useNewUnverifiedMembers = () => {
  const { user } = useAuth();
  const [hasNewUnverifiedMembers, setHasNewUnverifiedMembers] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkNewUnverifiedMembers = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        // First check if user is admin
        const { data: profile } = await supabase
          .from("profiles")
          .select("is_admin, display_name")
          .eq("id", user.id)
          .single();

        const userIsAdmin = profile?.is_admin || profile?.display_name === 'miikeu';
        setIsAdmin(userIsAdmin);

        // Only check for new members if user is admin
        if (!userIsAdmin) {
          setIsLoading(false);
          return;
        }

        // Get last visit timestamp from localStorage
        const lastVisit = localStorage.getItem('admin_members_last_visit');
        const lastVisitDate = lastVisit ? new Date(lastVisit) : null;

        // Calculate date 2 days ago as fallback
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

        // Use last visit date if available, otherwise use 2 days ago
        const checkDate = lastVisitDate || twoDaysAgo;

        // Check for members created after the check date that are not verified
        const { data, error } = await supabase
          .from("profiles")
          .select("id")
          .eq("is_verified", false)
          .gte("created_at", checkDate.toISOString())
          .limit(1);

        if (error) {
          console.error("Error checking new unverified members:", error);
          setHasNewUnverifiedMembers(false);
        } else {
          setHasNewUnverifiedMembers((data?.length || 0) > 0);
        }
      } catch (error) {
        console.error("Error checking new unverified members:", error);
        setHasNewUnverifiedMembers(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkNewUnverifiedMembers();

    // Refresh every 5 minutes for admins
    intervalId = setInterval(() => {
      checkNewUnverifiedMembers();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user?.id]);

  return { hasNewUnverifiedMembers, isLoading };
};

